import React, { useEffect, useMemo, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import {
  LineChart, Line, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
  BarChart, Bar, Cell, ReferenceLine,
} from "recharts";
import { Flame, Download, Calendar as CalIcon, ChevronLeft, ChevronRight } from "lucide-react";

const MONTH_NAMES = [
  "Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
  "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre",
];

const PAGE_SIZE = 31;

export default function SpotGas() {
  const now = new Date();
  const [available, setAvailable] = useState({ years: [], months: [] });
  const [availableLoaded, setAvailableLoaded] = useState(false);
  const [filterMode, setFilterMode] = useState("month"); // "month" | "range"
  const [year, setYear] = useState(now.getFullYear());
  const [month, setMonth] = useState(now.getMonth() + 1);
  const [dateFrom, setDateFrom] = useState("");
  const [dateTo, setDateTo] = useState("");
  const [unit, setUnit] = useState("mwh"); // mwh | smc
  const [rows, setRows] = useState([]);
  const [unitLabel, setUnitLabel] = useState("€/MWh");
  const [loading, setLoading] = useState(true);
  const [chart, setChart] = useState({ mode: "monthly", rows: [] });
  const [chartMode, setChartMode] = useState("monthly");
  const [page, setPage] = useState(1);
  const [downloading, setDownloading] = useState(false);

  // Load available months once and initialise selection on latest available.
  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/spot-gas/available-months");
        setAvailable(data);
        if (data.months.length > 0) {
          const [y, m] = data.months[0].split("-");
          setYear(Number(y));
          setMonth(Number(m));
        }
      } catch (e) {
        toast.error(e.response?.data?.detail || "Errore caricamento periodi");
      } finally {
        setAvailableLoaded(true);
      }
    })();
  }, []);

  // Load rows on filter change. In modalità "mese" attendo che available-months
  // abbia risposto — altrimenti il default (settembre 2026) farebbe una prima
  // chiamata a vuoto che può vincere la race con quella "buona" (agosto 2026).
  // Se il DB è vuoto (availableLoaded=true, months.length=0) esco subito con
  // rows=[] e loading=false così l'empty state viene mostrato.
  const loadRows = async () => {
    if (filterMode === "month" && !availableLoaded) return; // wait for months list
    if (filterMode === "month" && available.months.length === 0) {
      setRows([]);
      setLoading(false);
      return;
    }
    setLoading(true);
    try {
      const params = { unit };
      if (filterMode === "range") {
        if (dateFrom) params.date_from = dateFrom;
        if (dateTo) params.date_to = dateTo;
      } else {
        params.year = year;
        params.month = month;
      }
      const { data } = await api.get("/spot-gas/prices", { params });
      setRows(data.rows || []);
      setUnitLabel(data.unit);
      setPage(1);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore caricamento prezzi");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { loadRows(); /* eslint-disable-next-line */ }, [filterMode, year, month, dateFrom, dateTo, unit, availableLoaded, available.months.length]);

  // Load chart
  const loadChart = async () => {
    try {
      const { data } = await api.get("/spot-gas/chart", { params: { mode: chartMode, unit } });
      setChart(data);
    } catch (e) {
      // silent
    }
  };
  useEffect(() => { loadChart(); /* eslint-disable-next-line */ }, [chartMode, unit]);

  const digits = unit === "smc" ? 4 : 3;
  const chartDigits = unit === "smc" ? 3 : 2;
  const fmtChart = (v) => (v == null ? "—" : v.toFixed(chartDigits).replace(".", ","));
  const fmt = (v) => (v == null ? "—" : v.toFixed(digits).replace(".", ","));
  const fmtDelta = (v) => (v == null ? "—" : (v > 0 ? "+" : "") + v.toFixed(digits).replace(".", ","));

  // Zoom automatico asse Y: dataMin/dataMax con padding ~15% del range per rendere leggibili piccole variazioni
  const chartYDomain = useMemo(() => {
    const vals = (chart.rows || []).flatMap((r) => [r.psv, r.ttf]).filter((v) => v != null);
    if (vals.length === 0) return ["auto", "auto"];
    const min = Math.min(...vals);
    const max = Math.max(...vals);
    const range = max - min;
    const pad = range > 0 ? range * 0.15 : Math.max(max * 0.02, 0.1);
    return [+(min - pad).toFixed(4), +(max + pad).toFixed(4)];
  }, [chart.rows]);

  // Delta PSV-TTF (spread) per bar chart
  const deltaChartData = useMemo(() => {
    return (chart.rows || [])
      .filter((r) => r.psv != null && r.ttf != null)
      .map((r) => ({ label: r.label, delta: +(r.psv - r.ttf).toFixed(6) }));
  }, [chart.rows]);
  const hasSpotDelta = deltaChartData.length > 0;
  const deltaAbsMax = hasSpotDelta ? Math.max(...deltaChartData.map((d) => Math.abs(d.delta))) : 0;
  const spotDeltaDomain = deltaAbsMax > 0 ? [-deltaAbsMax * 1.2, deltaAbsMax * 1.2] : [-1, 1];

  const totalPages = Math.max(1, Math.ceil(rows.length / PAGE_SIZE));
  const pageRows = useMemo(() => {
    const start = (page - 1) * PAGE_SIZE;
    return rows.slice(start, start + PAGE_SIZE);
  }, [rows, page]);

  // Summary stats
  const stats = useMemo(() => {
    const psv = rows.map((r) => r.psv).filter((v) => v != null);
    const ttf = rows.map((r) => r.ttf).filter((v) => v != null);
    const deltas = rows
      .filter((r) => r.psv != null && r.ttf != null)
      .map((r) => r.psv - r.ttf);
    const stat = (arr) => arr.length ? {
      avg: arr.reduce((a, b) => a + b, 0) / arr.length,
      min: Math.min(...arr),
      max: Math.max(...arr),
    } : null;
    return { psv: stat(psv), ttf: stat(ttf), delta: stat(deltas) };
  }, [rows]);

  const download = async () => {
    setDownloading(true);
    try {
      const params = new URLSearchParams({ unit });
      if (filterMode === "range") {
        if (dateFrom) params.set("date_from", dateFrom);
        if (dateTo) params.set("date_to", dateTo);
      } else {
        params.set("year", String(year));
        params.set("month", String(month));
      }
      const res = await api.get(`/spot-gas/prices/export.xlsx?${params.toString()}`, { responseType: "blob" });
      const blob = new Blob([res.data], { type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      const suffix = filterMode === "range"
        ? `${dateFrom || "inizio"}_a_${dateTo || "oggi"}`
        : `${year}-${String(month).padStart(2, "0")}`;
      a.download = `adybo_psv-ttf_da_mid_${suffix}.xlsx`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("Excel scaricato");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore download");
    } finally {
      setDownloading(false);
    }
  };

  const prettyMonth = (ymStr) => {
    const [y, m] = ymStr.split("-");
    return `${MONTH_NAMES[Number(m) - 1].slice(0, 3)} ${y}`;
  };

  // Format date "YYYY-MM-DD" → "DD/MM/YYYY"
  const fmtDay = (d) => {
    if (!d) return "";
    const [y, mo, da] = d.split("-");
    return `${da}/${mo}/${y}`;
  };

  return (
    <div className="space-y-6" data-testid="spot-gas-page">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <h1 className="font-display text-3xl font-bold tracking-tight text-slate-900 flex items-center gap-3">
            <Flame className="text-blue-600" size={28} /> PSV - TTF DA Mid
          </h1>
          <p className="text-sm text-slate-500 mt-1">
            Prezzi giornalieri Day Ahead Mid (PSV e TTF). Unità: <span className="font-semibold">{unitLabel}</span>
            {unit === "smc" && " · fattore 1,05833"}
          </p>
        </div>
        <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 shadow-inner">
          <button
            onClick={() => setUnit("mwh")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold ${unit === "mwh" ? "bg-white shadow text-blue-600" : "text-slate-500"}`}
            data-testid="spot-unit-mwh"
          >€/MWh</button>
          <button
            onClick={() => setUnit("smc")}
            className={`px-3 py-1.5 rounded-md text-xs font-semibold ${unit === "smc" ? "bg-white shadow text-blue-600" : "text-slate-500"}`}
            data-testid="spot-unit-smc"
          >c€/Smc</button>
        </div>
      </div>

      {/* Chart card */}
      <div className="card-elevated p-5" data-testid="spot-chart-card">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-4">
          <div>
            <h2 className="font-display font-bold text-slate-900">Andamento ultimi 12 mesi</h2>
            <p className="text-xs text-slate-500">Serie {chartMode === "monthly" ? "mensile (media)" : "giornaliera"} in {unitLabel}</p>
          </div>
          <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 shadow-inner">
            <button
              onClick={() => setChartMode("monthly")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${chartMode === "monthly" ? "bg-white shadow text-blue-600" : "text-slate-500"}`}
              data-testid="spot-chart-monthly"
            >Mensile</button>
            <button
              onClick={() => setChartMode("daily")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${chartMode === "daily" ? "bg-white shadow text-blue-600" : "text-slate-500"}`}
              data-testid="spot-chart-daily"
            >Giornaliero</button>
          </div>
        </div>
        <div className="h-72">
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={chart.rows} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
              <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
              <XAxis
                dataKey="label"
                tick={{ fontSize: 11, fill: "#64748b" }}
                tickFormatter={(v) => chartMode === "monthly" ? prettyMonth(v) : v.slice(5)}
                minTickGap={20}
              />
              <YAxis
                tick={{ fontSize: 11, fill: "#64748b" }}
                width={65}
                domain={chartYDomain}
                tickFormatter={(v) => fmtChart(v)}
                allowDecimals
              />
              <Tooltip
                formatter={(v) => v == null ? "—" : `${fmtChart(v)} ${unitLabel}`}
                labelFormatter={(l) => chartMode === "monthly" ? prettyMonth(l) : fmtDay(l)}
                contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
              />
              <Legend wrapperStyle={{ fontSize: 12 }} />
              <Line type="monotone" dataKey="psv" name="PSV" stroke="#7c3aed" strokeWidth={2} dot={chartMode === "monthly"} />
              <Line type="monotone" dataKey="ttf" name="TTF" stroke="#0b4dff" strokeWidth={2} dot={chartMode === "monthly"} />
            </LineChart>
          </ResponsiveContainer>
        </div>
        {hasSpotDelta && (
          <div className="mt-4 pt-4 border-t border-slate-100" data-testid="spot-delta-chart">
            <div className="flex items-center justify-between mb-2 flex-wrap gap-1">
              <div className="text-xs font-semibold text-slate-700">Δ PSV − TTF ({unitLabel})</div>
              <div className="flex items-center gap-3 text-[10px] text-slate-500">
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-rose-500" /> PSV &gt; TTF</span>
                <span className="flex items-center gap-1"><span className="inline-block w-2.5 h-2.5 rounded-sm bg-emerald-500" /> PSV &lt; TTF</span>
              </div>
            </div>
            <div className="h-32">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={deltaChartData} margin={{ top: 4, right: 30, left: 0, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                  <XAxis
                    dataKey="label"
                    tick={{ fontSize: 10, fill: "#64748b" }}
                    tickFormatter={(v) => chartMode === "monthly" ? prettyMonth(v) : v.slice(5)}
                    minTickGap={20}
                  />
                  <YAxis
                    tick={{ fontSize: 10, fill: "#64748b" }}
                    width={65}
                    domain={spotDeltaDomain}
                    tickFormatter={(v) => (v > 0 ? "+" : "") + fmtChart(v)}
                    allowDecimals
                  />
                  <ReferenceLine y={0} stroke="#94A3B8" strokeWidth={1} />
                  <Tooltip
                    formatter={(v) => v == null ? "—" : `${v > 0 ? "+" : ""}${fmtChart(v)} ${unitLabel}`}
                    labelFormatter={(l) => chartMode === "monthly" ? prettyMonth(l) : fmtDay(l)}
                    contentStyle={{ fontSize: 12, borderRadius: 8, border: "1px solid #e2e8f0" }}
                  />
                  <Bar dataKey="delta" radius={[3, 3, 0, 0]}>
                    {deltaChartData.map((d, i) => (
                      <Cell key={i} fill={d.delta > 0 ? "#EF4444" : d.delta < 0 ? "#10B981" : "#94A3B8"} />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        )}
      </div>

      {/* Filter bar */}
      <div className="card-elevated p-4">
        <div className="flex items-center justify-between flex-wrap gap-3 mb-3">
          <div className="inline-flex items-center bg-slate-100 rounded-lg p-1 shadow-inner">
            <button
              onClick={() => setFilterMode("month")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${filterMode === "month" ? "bg-white shadow text-blue-600" : "text-slate-500"}`}
              data-testid="spot-filter-month"
            >Mese</button>
            <button
              onClick={() => setFilterMode("range")}
              className={`px-3 py-1.5 rounded-md text-xs font-semibold ${filterMode === "range" ? "bg-white shadow text-blue-600" : "text-slate-500"}`}
              data-testid="spot-filter-range"
            >Intervallo date</button>
          </div>
          <button
            onClick={download}
            disabled={downloading || rows.length === 0}
            className="btn-brand flex items-center gap-2 text-sm disabled:opacity-60"
            data-testid="spot-export-xlsx"
          >
            <Download size={15} /> {downloading ? "…" : "Esporta Excel"}
          </button>
        </div>

        {filterMode === "month" ? (
          <div className="flex items-center gap-3 flex-wrap">
            <div className="flex items-center gap-2">
              <CalIcon size={14} className="text-slate-400" />
              <select
                value={year}
                onChange={(e) => setYear(Number(e.target.value))}
                className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500"
                data-testid="spot-year-select"
              >
                {(available.years.length ? available.years : [String(now.getFullYear())]).map((y) => (
                  <option key={y} value={y}>{y}</option>
                ))}
              </select>
              <select
                value={month}
                onChange={(e) => setMonth(Number(e.target.value))}
                className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500"
                data-testid="spot-month-select"
              >
                {MONTH_NAMES.map((n, i) => (
                  <option key={i + 1} value={i + 1}>{n}</option>
                ))}
              </select>
            </div>
          </div>
        ) : (
          <div className="flex items-center gap-3 flex-wrap">
            <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
              Da
              <input
                type="date"
                value={dateFrom}
                onChange={(e) => setDateFrom(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500"
                data-testid="spot-date-from"
              />
            </label>
            <label className="text-xs font-semibold text-slate-600 flex items-center gap-2">
              A
              <input
                type="date"
                value={dateTo}
                onChange={(e) => setDateTo(e.target.value)}
                className="px-3 py-2 rounded-lg border border-slate-200 text-sm bg-white outline-none focus:border-blue-500"
                data-testid="spot-date-to"
              />
            </label>
            {(dateFrom || dateTo) && (
              <button
                onClick={() => { setDateFrom(""); setDateTo(""); }}
                className="text-xs px-3 py-1.5 rounded-lg border border-slate-200 hover:border-red-400 hover:text-red-600 text-slate-600 font-semibold bg-white"
                data-testid="spot-range-reset"
              >Reset</button>
            )}
          </div>
        )}
      </div>

      {/* Stats */}
      {stats.psv || stats.ttf ? (
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {stats.psv && (
            <div className="card-elevated p-4" data-testid="spot-stats-psv">
              <div className="text-xs font-semibold uppercase tracking-widest text-purple-600 mb-1">PSV — periodo</div>
              <div className="flex items-baseline gap-4 flex-wrap text-slate-700">
                <div><span className="text-slate-400 text-xs">Media</span> <span className="font-bold text-slate-900">{fmt(stats.psv.avg)}</span></div>
                <div><span className="text-slate-400 text-xs">Min</span> <span className="font-semibold">{fmt(stats.psv.min)}</span></div>
                <div><span className="text-slate-400 text-xs">Max</span> <span className="font-semibold">{fmt(stats.psv.max)}</span></div>
                <div className="text-xs text-slate-500">{unitLabel}</div>
              </div>
            </div>
          )}
          {stats.ttf && (
            <div className="card-elevated p-4" data-testid="spot-stats-ttf">
              <div className="text-xs font-semibold uppercase tracking-widest text-blue-600 mb-1">TTF — periodo</div>
              <div className="flex items-baseline gap-4 flex-wrap text-slate-700">
                <div><span className="text-slate-400 text-xs">Media</span> <span className="font-bold text-slate-900">{fmt(stats.ttf.avg)}</span></div>
                <div><span className="text-slate-400 text-xs">Min</span> <span className="font-semibold">{fmt(stats.ttf.min)}</span></div>
                <div><span className="text-slate-400 text-xs">Max</span> <span className="font-semibold">{fmt(stats.ttf.max)}</span></div>
                <div className="text-xs text-slate-500">{unitLabel}</div>
              </div>
            </div>
          )}
          {stats.delta && (
            <div className="card-elevated p-4" data-testid="spot-stats-delta">
              <div className="text-xs font-semibold uppercase tracking-widest text-slate-600 mb-1">Δ PSV−TTF — periodo</div>
              <div className="flex items-baseline gap-4 flex-wrap text-slate-700">
                <div>
                  <span className="text-slate-400 text-xs">Media</span>{" "}
                  <span className={`font-bold ${stats.delta.avg <= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtDelta(stats.delta.avg)}</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs">Min</span>{" "}
                  <span className={`font-semibold ${stats.delta.min <= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtDelta(stats.delta.min)}</span>
                </div>
                <div>
                  <span className="text-slate-400 text-xs">Max</span>{" "}
                  <span className={`font-semibold ${stats.delta.max <= 0 ? "text-emerald-600" : "text-red-600"}`}>{fmtDelta(stats.delta.max)}</span>
                </div>
                <div className="text-xs text-slate-500">{unitLabel}</div>
              </div>
              <div className="text-[11px] text-slate-400 mt-1">
                {stats.delta.avg <= 0 ? "PSV mediamente più basso del TTF (spread negativo)" : "PSV mediamente più alto del TTF (spread positivo)"}
              </div>
            </div>
          )}
        </div>
      ) : null}

      {/* Table */}
      <div className="card-elevated overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-100 flex items-center justify-between gap-2 flex-wrap">
          <div className="font-display font-semibold text-slate-900">
            Prezzi giornalieri
            <span className="ml-2 text-xs font-normal text-slate-400">({rows.length} gg)</span>
          </div>
          {totalPages > 1 && (
            <div className="flex items-center gap-2 text-xs text-slate-500">
              <button
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-40"
                data-testid="spot-page-prev"
              ><ChevronLeft size={14} /></button>
              <span>Pagina {page}/{totalPages}</span>
              <button
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="p-1 rounded hover:bg-slate-100 disabled:opacity-40"
                data-testid="spot-page-next"
              ><ChevronRight size={14} /></button>
            </div>
          )}
        </div>
        {loading ? (
          <div className="p-10 text-center text-sm text-slate-400">Caricamento…</div>
        ) : rows.length === 0 ? (
          <div className="p-10 text-center text-sm text-slate-500" data-testid="spot-empty">
            Nessun dato disponibile per il periodo selezionato.
          </div>
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 text-slate-600 text-xs uppercase tracking-wider">
                <tr>
                  <th className="text-left px-5 py-2.5 font-semibold">Giorno gas</th>
                  <th className="text-right px-5 py-2.5 font-semibold text-purple-600">PSV ({unitLabel})</th>
                  <th className="text-right px-5 py-2.5 font-semibold text-blue-600">TTF ({unitLabel})</th>
                  <th className="text-right px-5 py-2.5 font-semibold text-slate-600" title="Delta giornaliero: PSV meno TTF. Verde se PSV è più basso del TTF, rosso se più alto.">Δ PSV−TTF</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {pageRows.map((r) => {
                  const delta = (r.psv != null && r.ttf != null) ? r.psv - r.ttf : null;
                  const deltaColor = delta == null ? "text-slate-400" : (delta <= 0 ? "text-emerald-600" : "text-red-600");
                  return (
                    <tr key={r.date} className="hover:bg-slate-50/60" data-testid={`spot-row-${r.date}`}>
                      <td className="px-5 py-2 font-mono text-slate-700">{fmtDay(r.date)}</td>
                      <td className="px-5 py-2 text-right font-mono tabular-nums text-slate-900">{fmt(r.psv)}</td>
                      <td className="px-5 py-2 text-right font-mono tabular-nums text-slate-900">{fmt(r.ttf)}</td>
                      <td className={`px-5 py-2 text-right font-mono tabular-nums font-semibold ${deltaColor}`} data-testid={`spot-delta-${r.date}`}>{fmtDelta(delta)}</td>
                    </tr>
                  );
                })}
              </tbody>
              {(stats.psv || stats.ttf) && (
                <tfoot className="bg-slate-100/70 border-t-2 border-slate-200">
                  <tr data-testid="spot-row-avg">
                    <td className="px-5 py-2.5 font-display font-bold text-slate-900 text-xs uppercase tracking-widest">
                      Media {filterMode === "month" ? MONTH_NAMES[month - 1] : "periodo"}
                    </td>
                    <td className="px-5 py-2.5 text-right font-mono tabular-nums font-bold text-purple-700">
                      {stats.psv ? fmt(stats.psv.avg) : "—"}
                    </td>
                    <td className="px-5 py-2.5 text-right font-mono tabular-nums font-bold text-blue-700">
                      {stats.ttf ? fmt(stats.ttf.avg) : "—"}
                    </td>
                    <td className={`px-5 py-2.5 text-right font-mono tabular-nums font-bold ${
                      stats.delta ? (stats.delta.avg <= 0 ? "text-emerald-700" : "text-red-700") : "text-slate-400"
                    }`} data-testid="spot-delta-avg">
                      {stats.delta ? fmtDelta(stats.delta.avg) : "—"}
                    </td>
                  </tr>
                </tfoot>
              )}
            </table>
          </div>
        )}
      </div>
    </div>
  );
}
